/**
 * SystemHealthBanner - Accounting ana sayfasi ust kismi.
 * Her issue:
 *  - Onar butonu (uygun ise)
 *  - "Detay (n)" dropdown ile sample'lari gosterir
 *  - Her sample yaninda "Boz" mini butonu (tek-tek temizlik)
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, RefreshCw,
    Wrench, Loader2, ExternalLink, X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmt = (v) => (Number(v) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Hangi issue tipinde tek-tek boz desteklenir
const SINGLE_REPAIR = {
    alloc_mismatch: { path: 'repair-alloc-mismatch', key: 'tx_ids', idField: 'tx_id', label: 'Boz' },
    over_paid_via_multi_tx: { path: 'repair-over-paid', key: 'invoice_ids', idField: 'invoice_id', label: 'FIFO Onar' },
    ghost_matched: { path: 'repair-ghost-matched', key: 'tx_ids', idField: 'id', label: 'Unmatch' },
    direction_mismatch: { path: 'repair-direction-mismatch', key: 'tx_ids', idField: 'tx_id', label: 'Boz' },
    status_drift: { path: 'repair-status-drift', key: 'invoice_ids', idField: 'id', label: 'Recompute' },
    ghost_paid: { path: 'repair-ghost-paid', key: 'invoice_ids', idField: 'id', label: 'Sifirla' },
};

export default function SystemHealthBanner() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);
    const [repairing, setRepairing] = useState(false);
    const [openSamples, setOpenSamples] = useState({}); // {code: bool}
    const [singleBusy, setSingleBusy] = useState(null); // sample doc id

    const load = () => {
        setLoading(true);
        axios.get(`${API}/api/accounting/health-check`, { headers: headers() })
            .then(r => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const repairAllocMismatch = async () => {
        try {
            const dry = await axios.post(`${API}/api/accounting/health-check/repair-alloc-mismatch`,
                { dry_run: true }, { headers: headers() });
            const n = dry.data.repaired_count;
            const skipped = dry.data.skipped_within_tolerance;
            if (n === 0) { toast.info('Onarilacak mismatch yok'); load(); return; }
            const msg = `${n} eslesme bozulacak (tutar farki >1 EUR). ${skipped} tolerans icinde, dokunulmayacak.\n\nDevam et?`;
            if (!window.confirm(msg)) return;
            setRepairing(true);
            const r = await axios.post(`${API}/api/accounting/health-check/repair-alloc-mismatch`,
                { dry_run: false }, { headers: headers() });
            toast.success(`${r.data.repaired_count} eslesme bozuldu`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Onarim hatasi');
        } finally { setRepairing(false); }
    };

    const repairOverPaid = async () => {
        try {
            const dry = await axios.post(`${API}/api/accounting/health-check/repair-over-paid`,
                { dry_run: true, aggressive: true }, { headers: headers() });
            const n = dry.data.invoices_affected;
            const u = dry.data.total_tx_unlinked;
            if (u === 0) { toast.info('Onarilacak fazla odeme yok'); load(); return; }
            if (!window.confirm(`${n} fatura · ${u} tx bozulacak (FIFO + aggressive).\n\nDevam et?`)) return;
            setRepairing(true);
            const r = await axios.post(`${API}/api/accounting/health-check/repair-over-paid`,
                { dry_run: false, aggressive: true }, { headers: headers() });
            toast.success(`${r.data.invoices_affected} fatura · ${r.data.total_tx_unlinked} tx bozuldu`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Onarim hatasi');
        } finally { setRepairing(false); }
    };

    const repairDoublePayment = async (samples, action) => {
        try {
            const tx_ids = (samples || []).flatMap(s => s.tx_ids || []);
            if (tx_ids.length === 0) { toast.info('Tx bulunamadi'); return; }
            const label = action === 'delete' ? 'SILINECEK' : 'AVANS ISARETLENECEK';
            if (!window.confirm(`${tx_ids.length} cift odeme tx'i ${label}.\n\nDevam et?`)) return;
            setRepairing(true);
            const r = await axios.post(`${API}/api/accounting/health-check/repair-double-payment`,
                { tx_ids, action }, { headers: headers() });
            toast.success(`${r.data.affected} kayit islendi`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Hata');
        } finally { setRepairing(false); }
    };

    const repairBulkPayments = async () => {
        try {
            const dry = await axios.post(`${API}/api/accounting/health-check/repair-bulk-payments`,
                { dry_run: true }, { headers: headers() });
            const n = dry.data.candidate_count;
            if (n === 0) { toast.info('Onarilacak toplu odeme yok'); load(); return; }
            const totalToLink = (dry.data.samples || []).reduce((s, x) => s + (x.to_link_invoices?.length || 0), 0);
            if (!window.confirm(`${n} transferin communication'i parse edilecek, toplam ~${totalToLink} fatura otomatik baglanacak. Devam et?`)) return;
            setRepairing(true);
            const r = await axios.post(`${API}/api/accounting/health-check/repair-bulk-payments`,
                { dry_run: false }, { headers: headers() });
            toast.success(`${r.data.repaired_transactions} transfer · ${r.data.total_invoices_linked} fatura otomatik baglandi`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Onarim hatasi');
        } finally { setRepairing(false); }
    };

    const genericRepair = async (path, label) => {
        try {
            const dry = await axios.post(`${API}/api/accounting/health-check/${path}`,
                { dry_run: true }, { headers: headers() });
            const n = dry.data.found ?? dry.data.ghost_count ?? 0;
            if (n === 0) { toast.info(`Onarilacak ${label} yok`); load(); return; }
            if (!window.confirm(`${n} kayit isleneecek (${label}). Devam et?`)) return;
            setRepairing(true);
            const r = await axios.post(`${API}/api/accounting/health-check/${path}`,
                { dry_run: false }, { headers: headers() });
            toast.success(`${r.data.repaired_count} kayit onarildi`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Onarim hatasi');
        } finally { setRepairing(false); }
    };

    const singleRepair = async (issueCode, sample) => {
        const cfg = SINGLE_REPAIR[issueCode];
        if (!cfg) return;
        const id = sample[cfg.idField];
        if (!id) return toast.error('ID bulunamadi');
        setSingleBusy(id);
        try {
            const body = { dry_run: false, [cfg.key]: [id] };
            if (issueCode === 'over_paid_via_multi_tx') body.aggressive = true;
            const r = await axios.post(`${API}/api/accounting/health-check/${cfg.path}`,
                body, { headers: headers() });
            const n = r.data.repaired_count ?? r.data.total_tx_unlinked ?? 1;
            toast.success(`Onarildi (${n})`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Hata');
        } finally { setSingleBusy(null); }
    };

    if (loading || !data) return null;
    if (data.ok) {
        return (
            <div className="bg-emerald-50/60 border border-emerald-200 rounded-xl px-3 py-2 flex items-center justify-between gap-2" data-testid="acc-health-banner-ok">
                <div className="flex items-center gap-2 text-[12px] text-emerald-800">
                    <CheckCircle2 size={14} className="text-emerald-600" />
                    <span><strong>Veri Sagligi:</strong> Tum kontroller temiz</span>
                </div>
                <button onClick={load} className="text-[11px] text-emerald-700 hover:underline flex items-center gap-1" data-testid="acc-health-refresh">
                    <RefreshCw size={11} /> Yenile
                </button>
            </div>
        );
    }
    const hasError = data.issues.some(i => i.severity === 'error');
    const tone = hasError ? 'red' : 'amber';
    const toneCls = tone === 'red' ? 'bg-red-50 border-red-200 text-red-900' : 'bg-amber-50 border-amber-200 text-amber-900';

    return (
        <div className={`rounded-xl border ${toneCls}`} data-testid="acc-health-banner">
            <button onClick={() => setExpanded(!expanded)}
                className="w-full px-3 py-2 flex items-center justify-between gap-2 text-left"
                data-testid="acc-health-toggle">
                <div className="flex items-center gap-2 text-[12px]">
                    <AlertTriangle size={14} className={tone === 'red' ? 'text-red-600' : 'text-amber-600'} />
                    <span>
                        <strong>{data.issue_count} Veri Tutarsizligi</strong> tespit edildi
                        {hasError && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 bg-red-200 text-red-800 rounded font-bold">KRITIK</span>}
                    </span>
                </div>
                {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {expanded && (
                <div className="px-3 pb-3 space-y-2 border-t border-white/40 pt-2" data-testid="acc-health-issues">
                    {data.issues.map((iss, i) => {
                        const supportsSingle = !!SINGLE_REPAIR[iss.code];
                        const samplesOpen = openSamples[iss.code];
                        const hasSamples = (iss.samples || []).length > 0;
                        return (
                            <div key={i} className="bg-white/70 rounded-lg p-2.5" data-testid={`acc-health-issue-${iss.code}`}>
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${iss.severity === 'error' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                                        {iss.severity === 'error' ? 'HATA' : 'UYARI'}
                                    </span>
                                    <span className="text-[12px] font-semibold text-[#111]">{iss.title}</span>
                                    <span className="text-[10px] text-[#6B7280]">({iss.count})</span>
                                    {iss.code === 'alloc_mismatch' && (
                                        <button onClick={repairAllocMismatch} disabled={repairing}
                                            className="ml-auto h-7 px-2.5 bg-red-500 hover:bg-red-600 text-white text-[11px] font-bold rounded-md flex items-center gap-1 disabled:opacity-50"
                                            data-testid="acc-health-repair-alloc">
                                            {repairing ? <Loader2 size={11} className="animate-spin" /> : <Wrench size={11} />}
                                            Hepsini Onar
                                        </button>
                                    )}
                                    {iss.code === 'over_paid_via_multi_tx' && (
                                        <button onClick={repairOverPaid} disabled={repairing}
                                            className="ml-auto h-7 px-2.5 bg-red-500 hover:bg-red-600 text-white text-[11px] font-bold rounded-md flex items-center gap-1 disabled:opacity-50"
                                            data-testid="acc-health-repair-overpaid">
                                            {repairing ? <Loader2 size={11} className="animate-spin" /> : <Wrench size={11} />}
                                            Hepsini Onar (FIFO)
                                        </button>
                                    )}
                                    {iss.code === 'ghost_matched' && (
                                        <button onClick={() => genericRepair('repair-ghost-matched', 'hayalet eslesme')} disabled={repairing}
                                            className="ml-auto h-7 px-2.5 bg-red-500 hover:bg-red-600 text-white text-[11px] font-bold rounded-md flex items-center gap-1 disabled:opacity-50"
                                            data-testid="acc-health-repair-ghost">
                                            {repairing ? <Loader2 size={11} className="animate-spin" /> : <Wrench size={11} />}
                                            Hepsini Onar
                                        </button>
                                    )}
                                    {iss.code === 'direction_mismatch' && (
                                        <button onClick={() => genericRepair('repair-direction-mismatch', 'yon cakismasi')} disabled={repairing}
                                            className="ml-auto h-7 px-2.5 bg-red-500 hover:bg-red-600 text-white text-[11px] font-bold rounded-md flex items-center gap-1 disabled:opacity-50"
                                            data-testid="acc-health-repair-direction">
                                            {repairing ? <Loader2 size={11} className="animate-spin" /> : <Wrench size={11} />}
                                            Hepsini Boz
                                        </button>
                                    )}
                                    {iss.code === 'status_drift' && (
                                        <button onClick={() => genericRepair('repair-status-drift', 'status drift')} disabled={repairing}
                                            className="ml-auto h-7 px-2.5 bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-bold rounded-md flex items-center gap-1 disabled:opacity-50"
                                            data-testid="acc-health-repair-status-drift">
                                            {repairing ? <Loader2 size={11} className="animate-spin" /> : <Wrench size={11} />}
                                            Hepsini Recompute
                                        </button>
                                    )}
                                    {iss.code === 'ghost_paid' && (
                                        <button onClick={() => genericRepair('repair-ghost-paid', 'hayalet odeme')} disabled={repairing}
                                            className="ml-auto h-7 px-2.5 bg-red-500 hover:bg-red-600 text-white text-[11px] font-bold rounded-md flex items-center gap-1 disabled:opacity-50"
                                            data-testid="acc-health-repair-ghost-paid">
                                            {repairing ? <Loader2 size={11} className="animate-spin" /> : <Wrench size={11} />}
                                            Hepsini Sifirla
                                        </button>
                                    )}
                                    {iss.code === 'bulk_payments_unlinked' && (
                                        <button onClick={repairBulkPayments} disabled={repairing}
                                            className="ml-auto h-7 px-2.5 bg-indigo-500 hover:bg-indigo-600 text-white text-[11px] font-bold rounded-md flex items-center gap-1 disabled:opacity-50"
                                            data-testid="acc-health-repair-bulk-payments">
                                            {repairing ? <Loader2 size={11} className="animate-spin" /> : <Wrench size={11} />}
                                            Otomatik Eslestir
                                        </button>
                                    )}
                                    {iss.code === 'possible_double_payment' && (
                                        <button onClick={() => repairDoublePayment(iss.samples || [], 'mark_advance')} disabled={repairing}
                                            className="ml-auto h-7 px-2.5 bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-bold rounded-md flex items-center gap-1 disabled:opacity-50"
                                            data-testid="acc-health-repair-double-payment">
                                            {repairing ? <Loader2 size={11} className="animate-spin" /> : <Wrench size={11} />}
                                            Hepsini Avans Yap
                                        </button>
                                    )}
                                    {(['cross_contact_duplicate', 'orphan_invoices', 'cross_iban_duplicate', 'cross_vat_duplicate'].includes(iss.code)) && (
                                        <button onClick={() => navigate('/accounting/duplicates')}
                                            className="ml-auto h-7 px-2.5 bg-indigo-500 hover:bg-indigo-600 text-white text-[11px] font-bold rounded-md flex items-center gap-1"
                                            data-testid={`acc-health-goto-dup-${iss.code}`}>
                                            <ExternalLink size={11} /> Duplicates Sayfasi
                                        </button>
                                    )}
                                </div>
                                <p className="text-[11px] text-[#374151]">{iss.message}</p>
                                {iss.action_hint && (
                                    <p className="text-[10px] text-[#6B7280] mt-1">→ {iss.action_hint}</p>
                                )}
                                {/* Detay dropdown */}
                                {hasSamples && (
                                    <button onClick={() => setOpenSamples(s => ({ ...s, [iss.code]: !s[iss.code] }))}
                                        className="mt-2 text-[11px] font-semibold text-[#374151] hover:text-[#111] flex items-center gap-1"
                                        data-testid={`acc-health-detail-toggle-${iss.code}`}>
                                        {samplesOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                                        Detay ({iss.samples.length} ornek)
                                    </button>
                                )}
                                {hasSamples && samplesOpen && (
                                    <div className="mt-2 bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid={`acc-health-detail-${iss.code}`}>
                                        <SampleTable
                                            code={iss.code}
                                            samples={iss.samples}
                                            supportsSingle={supportsSingle}
                                            onSingleRepair={(s) => singleRepair(iss.code, s)}
                                            singleBusy={singleBusy}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    <button onClick={load} className="text-[11px] underline" data-testid="acc-health-refresh">Yenile</button>
                </div>
            )}
        </div>
    );
}

function SampleTable({ code, samples, supportsSingle, onSingleRepair, singleBusy }) {
    // Issue tipine gore farkli kolonlar
    const cfg = (() => {
        switch (code) {
            case 'alloc_mismatch':
                return {
                    cols: ['Tarih', 'Karsi Taraf', 'Tutar', 'Alloc', 'Fark'],
                    row: s => [s.date, s.counterparty, fmt(s.tx_amount), fmt(s.alloc_sum), fmt(s.diff)],
                };
            case 'over_paid_via_multi_tx':
                return {
                    cols: ['Fatura No', 'Firma', 'Inv Total', 'Tx Total', 'Fazla'],
                    row: s => [s.invoice_number, s.contact_name, fmt(s.invoice_total), fmt(s.tx_total), fmt(s.over_amount)],
                };
            case 'ghost_matched':
                return {
                    cols: ['Tarih', 'Karsi Taraf', 'Aciklama', 'Tutar'],
                    row: s => [s.transaction_date, s.counterparty_name, s.communication, fmt(s.amount)],
                };
            case 'direction_mismatch':
                return {
                    cols: ['Tarih', 'Karsi Taraf', 'Tx Tutar', 'Fatura', 'Tip', 'Inv Total'],
                    row: s => [s.transaction_date, s.counterparty_name, fmt(s.tx_amount), s.invoice_number, s.invoice_type, fmt(s.invoice_total)],
                };
            case 'currency_mismatch':
                return {
                    cols: ['Tarih', 'Tx CCY', 'Tutar', 'Fatura', 'Inv CCY'],
                    row: s => [s.transaction_date, s.tx_currency, fmt(s.amount), s.invoice_number, s.invoice_currency],
                };
            case 'status_drift':
                return {
                    cols: ['Fatura No', 'Firma', 'Total', 'Paid', 'Status', 'Tip'],
                    row: s => [s.invoice_number, s.contact_name, fmt(s.total), fmt(s.paid_amount), s.status, s.type],
                };
            case 'ghost_paid':
                return {
                    cols: ['Fatura No', 'Firma', 'Tarih', 'Total', 'Sahte Paid', 'Status'],
                    row: s => [s.invoice_number, s.contact_name, s.invoice_date, fmt(s.total), fmt(s.paid_amount), s.status],
                };
            case 'bulk_payments_unlinked':
                return {
                    cols: ['Tarih', 'Tutar', 'Beklenen', 'Bagli', 'Eksik', 'Communication'],
                    row: s => [s.transaction_date, fmt(s.amount), s.expected_invoice_count, s.linked_count, s.missing, s.communication],
                };
            case 'type_sign_mismatch':
                return {
                    cols: ['Fatura No', 'Firma', 'Tip', 'Total'],
                    row: s => [s.invoice_number, s.contact_name, s.type, fmt(s.total)],
                };
            case 'over_allocated':
                return {
                    cols: ['Fatura No', 'Firma', 'Total', 'Paid', 'Fazla'],
                    row: s => [s.invoice_number, s.contact_name, fmt(s.total), fmt(s.paid_amount), fmt(s._excess)],
                };
            case 'orphan_invoices':
                return {
                    cols: ['Fatura No', 'Firma Adi', 'Tarih', 'Tutar'],
                    row: s => [s.invoice_number, s.contact_name, s.invoice_date, fmt(s.total)],
                };
            case 'cross_iban_duplicate':
                return {
                    cols: ['IBAN', '# Firma', 'Firmalar'],
                    row: s => [s.iban, s.company_count, (s.names || []).join(', ')],
                };
            case 'cross_vat_duplicate':
                return {
                    cols: ['KDV No', '# Firma', 'Firmalar'],
                    row: s => [s.vat_number, s.company_count, (s.names || []).join(', ')],
                };
            case 'cross_contact_duplicate':
                return {
                    cols: ['Fatura No', 'Tip', 'Tutar', '# Firma', 'Firmalar'],
                    row: s => [s.invoice_number, s.type, fmt(s.total), s.company_count, (s.names || []).join(', ')],
                };
            case 'possible_double_payment':
                return {
                    cols: ['Tarih', 'Karsi Taraf', 'Tutar', '# Tx', 'Ref'],
                    row: s => [s.transaction_date, s.counterparty_name, fmt(s.amount), s.tx_count, (s.bank_refs || []).filter(Boolean).join(' / ')],
                };
            default:
                return null;
        }
    })();

    if (!cfg) return <p className="p-3 text-[11px] text-[#9CA3AF]">Detay yok</p>;

    return (
        <div className="overflow-x-auto max-h-72">
            <table className="w-full text-[11px]">
                <thead className="bg-[#FAFAFA] sticky top-0">
                    <tr>
                        {cfg.cols.map(c => <th key={c} className="text-left px-2 py-1.5 font-semibold text-[#6B7280]">{c}</th>)}
                        {supportsSingle && <th className="px-2 py-1.5 w-20"></th>}
                    </tr>
                </thead>
                <tbody className="divide-y divide-[#F3F4F6]">
                    {samples.map((s, i) => {
                        const cells = cfg.row(s);
                        const sampleId = s.id || s.tx_id || s.invoice_id;
                        const busy = singleBusy === sampleId;
                        return (
                            <tr key={i} className="hover:bg-[#F9FAFB]">
                                {cells.map((c, j) => (
                                    <td key={j} className="px-2 py-1.5 text-[#111] truncate max-w-[160px]" title={String(c ?? '')}>{c ?? '-'}</td>
                                ))}
                                {supportsSingle && (
                                    <td className="px-2 py-1 text-right">
                                        <button onClick={() => onSingleRepair(s)} disabled={busy}
                                            className="h-6 px-2 bg-[#111] hover:bg-[#000] text-white text-[10px] font-bold rounded flex items-center gap-1 ml-auto disabled:opacity-50"
                                            data-testid={`acc-health-single-repair-${code}-${i}`}>
                                            {busy ? <Loader2 size={9} className="animate-spin" /> : <X size={9} />}
                                            {SINGLE_REPAIR[code].label}
                                        </button>
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
